import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo, useEffect } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { BaseRow } from '~/components/common';
import { useTranslation } from 'react-i18next';
import styles from './EditLocationDialog.module.scss';
import classNames from 'classnames/bind';
import { useAppDispatch } from '~/redux/hooks';
import { ICreateLocationCompany } from '~/utils/interface/location';
import { handleCreateLocationCompany, handleUpdateLocation } from '~/thunks/location/locationThunk';
import { locationActions } from '~/thunks/location/locationSlice';

const cx = classNames.bind(styles);
type EditLocationDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  mode?: string;
  title?: string;
  data?: ICreateLocationCompany;
  children?: React.ReactNode;
};

const initialValues: ICreateLocationCompany = {};

const EditLocationDialog = (props: EditLocationDialogProps) => {
  const { t } = useTranslation('admin/location');
  const { mode, data } = props;
  const dispatch = useAppDispatch();

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (mode === DIALOG_MODE_CREATE) {
      form.resetForm();
    }
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    dispatch(locationActions.changeMenuSelectId(undefined));
    dispatch(locationActions.changeSelectedIds([]));
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateLocationCompany(values)).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false);
          dispatch(locationActions.changeNeedRefreshData(true));
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        await dispatch(handleUpdateLocation({ id: data?.id, data: values })).unwrap();
        handleSetOpen(false);
        notify('success', t('editSuccess', { ns: 'admin/common' }));
        form.resetForm();
        dispatch(locationActions.changeNeedRefreshData(true));
        try {
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });

  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(data?.name));
      form.setFieldValue('code', cast(data?.code));
      form.setFieldValue('address', cast(data?.address));
      form.setFieldValue('address2', cast(data?.address2));
      form.setFieldValue('state', cast(data?.state));
      form.setFieldValue('zipCode', cast(data?.zipCode));
      form.setFieldValue('country', cast(data?.country));
      form.setFieldValue('city', cast(data?.city));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addLocation') : t('EditLocation')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel required={true} className={cx('LocationName')}>
                {t('name')}
              </InputLabel>
              <TextField
                name='name'
                placeholder={t('name')}
                value={form.values?.name}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
              />
            </Box>

            <Box className='w-full'>
              <InputLabel required={true} className={cx('tax')}>
                {t('code')}
              </InputLabel>
              <TextField
                name='code'
                placeholder={t('code')}
                value={form.values?.code}
                onChange={form.handleChange}
                error={Boolean(form.errors?.code)}
                helperText={form.errors?.code}
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[10px]'>
            <Box className='w-full'>
              <InputLabel className={cx('name')}>{t('address')}</InputLabel>
              <TextField
                name='address'
                placeholder={t('address')}
                value={form.values?.address}
                onChange={form.handleChange}
                error={Boolean(form.errors?.address)}
                helperText={form.errors?.address}
              />
            </Box>

            <Box className='w-full'>
              <InputLabel className={cx('LocationName')}>{t('address2')}</InputLabel>
              <TextField
                name='address2'
                placeholder={t('address2')}
                value={form.values?.address2}
                onChange={form.handleChange}
                error={Boolean(form.errors?.address2)}
                helperText={form.errors?.address2}
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel className={cx('LocationName')}>{t('state')}</InputLabel>
              <TextField
                name='state'
                placeholder={t('state')}
                value={form.values?.state}
                onChange={form.handleChange}
                error={Boolean(form.errors?.state)}
                helperText={form.errors?.state}
              />
            </Box>

            <Box className='w-full'>
              <InputLabel className={cx('LocationName')}>{t('zipCode')}</InputLabel>
              <TextField
                name='zipCode'
                placeholder={t('zipCode')}
                value={form.values?.zipCode}
                onChange={form.handleChange}
                error={Boolean(form.errors?.zipCode)}
                helperText={form.errors?.zipCode}
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel className={cx('LocationName')}>{t('country')}</InputLabel>
              <TextField
                name='country'
                placeholder={t('country')}
                value={form.values?.country}
                onChange={form.handleChange}
                error={Boolean(form.errors?.country)}
                helperText={form.errors?.country}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel className={cx('LocationName')}>{t('city')}</InputLabel>
              <TextField
                name='city'
                placeholder={t('city')}
                value={form.values?.city}
                onChange={form.handleChange}
                error={Boolean(form.errors?.city)}
                helperText={form.errors?.city}
              />
            </Box>
          </BaseRow>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={() => handleSetOpen(false)}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditLocationDialog);
